import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, money, fmtDate, DOC_LABELS, ApiError } from '../api'
import { Page } from '../App'
import { Card, Badge, Alert, Spinner, Empty } from '../components/ui'

interface Row {
  id: string
  number: string | null
  doc_type: string
  issue_date: string
  due_date: string
  currency: string
  total: number
  balance: number
  status: string
  client_legal_name: string
  client_display_name: string | null
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'open', label: 'Open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'paid', label: 'Paid' },
]

export default function Invoices() {
  const [rows, setRows] = useState<Row[]>([])
  const [summary, setSummary] = useState<{ n: number; sum_total: number; sum_balance: number } | null>(null)
  const [status, setStatus] = useState('all')
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams()
        if (status !== 'all') params.set('status', status)
        if (q) params.set('q', q)
        const res = await api.get<{ invoices: Row[]; summary: typeof summary }>(`/invoices?${params}`)
        setRows(res.invoices)
        setSummary(res.summary)
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Failed to load')
      } finally {
        setLoading(false)
      }
    }, q ? 250 : 0)
    return () => clearTimeout(t)
  }, [status, q])

  return (
    <Page
      title="Invoices"
      subtitle={
        summary
          ? `${summary.n} document${summary.n === 1 ? '' : 's'} · ${money(summary.sum_total)} billed · ${money(summary.sum_balance)} outstanding`
          : undefined
      }
      actions={
        <button className="btn primary" onClick={() => navigate('/invoices/new')}>
          + New invoice
        </button>
      }
    >
      <Alert kind="error">{error}</Alert>

      <div className="inline" style={{ marginBottom: 14 }}>
        <div className="pill-row" style={{ flex: 'none' }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`btn sm ${status === f.key ? 'primary' : ''}`}
              onClick={() => setStatus(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ maxWidth: 300 }}>
          <input
            type="text"
            placeholder="Search number, client or PO…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <Card tight>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty icon="▤" title="Nothing here" hint="Try a different filter, or create an invoice." />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Client</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Due</th>
                  <th className="r">Total</th>
                  <th className="r">Balance</th>
                  <th className="r">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => navigate(`/invoices/${r.id}`)}>
                    <td className="mono nowrap">{r.number ?? <span className="muted">Draft</span>}</td>
                    <td>{r.client_display_name || r.client_legal_name}</td>
                    <td className="small">{DOC_LABELS[r.doc_type] ?? r.doc_type}</td>
                    <td className="nowrap small">{fmtDate(r.issue_date)}</td>
                    <td className="nowrap small">{fmtDate(r.due_date)}</td>
                    <td className="num">{money(r.total, r.currency)}</td>
                    <td className="num">
                      {r.balance > 0 ? <strong>{money(r.balance, r.currency)}</strong> : <span className="muted">—</span>}
                    </td>
                    <td className="right">
                      <Badge status={r.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Page>
  )
}
